import { useCallback, useEffect, useMemo, useState } from "react";
import type { ColumnDef } from "@tanstack/react-table";

import { api, ApiFailure } from "../api/client";
import { usePagedResource } from "../api/usePagedResource";
import type { ImportSummary, Rate, RateDeck } from "../api/types";
import { DataTable } from "../components/data-table";
import {
  ActionError, Badge, Button, Card, ErrorState, Field, Loading, Modal, PageHeader,
  inputClass,
} from "../components/ui";
import { formatDateTime } from "./Calls";
import { money } from "./Dashboard";

// Tarifas: los rate decks y su importación por CSV. Solo plataforma.
//
// # Por qué la vista previa es obligatoria en la práctica
//
// Porque un cambio de precios mal hecho no se nota hasta la factura. El flujo
// de esta pantalla es: pegar el CSV, **ver qué cambia**, y solo entonces
// aplicar. El botón de aplicar no aparece hasta que hay una vista previa
// revisada.
export function Rates() {
  const [decks, setDecks] = useState<RateDeck[] | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creandoDeck, setCreandoDeck] = useState(false);
  const [importando, setImportando] = useState(false);
  // El resultado sobrevive al cierre del diálogo: es lo que contesta «¿se
  // aplicó?», y dentro del modal se iría con él.
  const [importado, setImportado] = useState<ImportSummary | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const rows = await api.get<RateDeck[]>("/api/v1/rate-decks");
      setDecks(rows ?? []);
      const first = rows?.[0];
      if (first) {
        setSelected((current) => current ?? first.id);
      }
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "error inesperado");
    }
  }, []);

  // Un rate deck de carrier trae miles de prefijos, así que las tarifas se
  // paginan. `null` mientras no hay deck elegido: el hook no pide nada.
  const tarifas = usePagedResource<Rate>(
    selected === null ? null : `/api/v1/rate-decks/${selected}/rates`,
    50,
  );
  const rates = tarifas.data ?? [];

  useEffect(() => {
    void load();
  }, [load]);

  const columnas = useMemo<ColumnDef<Rate, any>[]>(
    () => [
      {
        id: "prefix",
        accessorFn: (rate) => (rate.catch_all ? "comodín" : rate.prefix),
        header: "Prefijo",
        meta: { label: "Prefijo", width: "8rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-100">
            {row.original.catch_all ? (
              <em className="text-slate-500">comodín</em>
            ) : (
              `+${row.original.prefix}`
            )}
          </span>
        ),
      },
      {
        id: "description",
        accessorKey: "description",
        header: "Descripción",
        meta: { label: "Descripción" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">{row.original.description}</span>
        ),
      },
      {
        id: "price",
        accessorFn: (rate) => Number(rate.price),
        header: "Precio/min",
        meta: { label: "Precio/min", align: "right", width: "8rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums text-slate-100">
            {money(row.original.price)}
          </span>
        ),
      },
      {
        id: "redondeo",
        accessorFn: (rate) =>
          `${rate.initial_increment_seconds}/${rate.subsequent_increment_seconds}`,
        header: "Redondeo",
        enableSorting: false,
        meta: { label: "Redondeo", align: "center", hideOnMobile: true, width: "7rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs text-slate-600">
            {row.original.initial_increment_seconds}/{row.original.subsequent_increment_seconds}
          </span>
        ),
      },
      {
        id: "connection_charge_micros",
        accessorKey: "connection_charge_micros",
        header: "Conexión",
        meta: { label: "Conexión", align: "right", hideOnMobile: true, width: "8rem" },
        cell: ({ row }) => (
          <span className="font-mono text-xs tabular-nums text-slate-600">
            {row.original.connection_charge_micros === 0
              ? "—"
              : money((row.original.connection_charge_micros / 1_000_000).toFixed(6))}
          </span>
        ),
      },
      {
        id: "vigencia",
        // El valor es la etiqueta y no la fecha: es lo que filtra el
        // desplegable, donde la cadena vacía ya significa «todas».
        accessorFn: (rate) => (rate.effective_to ? "cerrada" : "vigente"),
        header: "Vigencia",
        meta: { label: "Vigencia" },
        cell: ({ row }) => (
          <span className="text-xs text-slate-600">
            {row.original.effective_to ? (
              <span>cerrada {formatDateTime(row.original.effective_to)}</span>
            ) : (
              <Badge tone="good">vigente</Badge>
            )}
          </span>
        ),
      },
    ],
    [],
  );

  if (decks === null && error === null) return <Loading />;
  if (decks === null) return <ErrorState message={error ?? ""} onRetry={() => void load()} />;

  const deckElegido = decks.find((d) => d.id === selected) ?? null;

  return (
    <div>
      <PageHeader
        title="Tarifas"
        description="Un rate deck por lista de precios. Las tarifas se versionan por vigencia: nunca se editan."
        action={
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" onClick={() => setCreandoDeck(true)}>
              Nuevo deck
            </Button>
            <Button
              disabled={deckElegido === null}
              onClick={() => {
                setImportado(null);
                setImportando(true);
              }}
            >
              Importar tarifas
            </Button>
          </div>
        }
      />

      {creandoDeck && (
        <NuevoDeck
          hasDefault={decks.some((d) => d.is_default)}
          onCancel={() => setCreandoDeck(false)}
          onCreated={(id) => {
            setCreandoDeck(false);
            setSelected(id);
            void load();
          }}
        />
      )}

      {importando && deckElegido !== null && (
        <ImportarTarifas
          deck={deckElegido}
          onCancel={() => setImportando(false)}
          onImported={(resumen) => {
            setImportando(false);
            setImportado(resumen);
            void load();
            // Importar sustituye el deck entero: hay que volver al principio
            // de la lista, no recargar la página donde se estaba.
            tarifas.reiniciar();
            void tarifas.reload(true);
          }}
        />
      )}

      {importado !== null && (
        <Card className="mb-4 flex flex-wrap items-center justify-between gap-3 border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-200">
          <span>
            Importado en «{deckElegido?.name}»: {importado.added} nuevas, {importado.changed}{" "}
            actualizadas, {importado.closed} cerradas.
          </span>
          <button className="text-xs underline hover:text-emerald-100" onClick={() => setImportado(null)}>
            Cerrar
          </button>
        </Card>
      )}

      {decks.length === 0 ? (
        <Card className="p-6 text-center">
          <p className="text-sm text-slate-300">Todavía no hay ningún rate deck.</p>
          <p className="mx-auto mt-1 max-w-prose text-xs text-slate-500">
            Sin uno, las llamadas se rechazan con <code>DESTINATION_NOT_RATED</code>: no se
            puede cobrar lo que no tiene precio.
          </p>
          <div className="mt-4">
            <Button onClick={() => setCreandoDeck(true)}>Crear el primer deck</Button>
          </div>
        </Card>
      ) : (
        // Tarjetas y no una lista: el deck elegido manda sobre todo lo que hay
        // debajo —la tabla y el importador—, así que tiene que verse cuál es
        // sin buscarlo.
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {decks.map((deck) => {
            const activo = selected === deck.id;
            return (
              <button
                key={deck.id}
                aria-pressed={activo}
                onClick={() => setSelected(deck.id)}
                className={
                  "rounded-xl border p-4 text-left transition " +
                  (activo
                    ? "border-sky-400/60 bg-sky-500/10 shadow-[0_0_0_1px_rgb(56,189,248,0.4)]"
                    : "border-white/10 bg-white/[0.02] hover:border-white/20 hover:bg-white/[0.05]")
                }
              >
                <div className="flex items-start justify-between gap-2">
                  <span className={activo ? "font-medium text-white" : "text-slate-200"}>
                    {deck.name}
                  </span>
                  {deck.is_default && <Badge tone="good">Por omisión</Badge>}
                </div>
                <div className="mt-3 flex items-baseline gap-1.5">
                  <span className="text-2xl font-semibold tabular-nums text-slate-100">
                    {deck.active_rates ?? 0}
                  </span>
                  <span className="text-xs text-slate-500">tarifas vigentes · {deck.currency}</span>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {selected && (
        <>
          <section className="mt-6">
            <h2 className="mb-2 text-sm font-medium text-slate-100">Tarifas del deck</h2>
            <p className="mb-3 text-xs text-slate-500">
              Las cerradas se quedan: una llamada se cobra con la tarifa que estaba vigente
              cuando empezó, así que borrarlas reescribiría el pasado.
            </p>
            <DataTable<Rate>
              data={rates}
              columns={columnas}
              getRowId={(rate) => rate.id}
              loading={tarifas.loading}
              error={tarifas.error}
              onRetry={() => void tarifas.reload(false)}
              caption="Tarifas del deck"
              empty={{
                title: "Sin tarifas",
                description: "Importa un CSV para poner precios a este deck.",
              }}
              search={{ placeholder: "Buscar por prefijo o descripción…" }}
              filters={[
                {
                  columnId: "vigencia",
                  label: "Vigencia",
                  options: [
                    { value: "vigente", label: "Vigentes" },
                    { value: "cerrada", label: "Cerradas" },
                  ],
                },
              ]}
              // Las cerradas se atenúan en lugar de desaparecer: forman parte
              // del historial con el que se cobró.
              rowClassName={(rate) => (rate.effective_to ? "opacity-50" : undefined)}
              hideableColumns
              urlStateKey="tarifas"
              pagination={{
                pageSize: tarifas.enEstaPagina,
                hasNext: tarifas.hayMas,
                hasPrevious: tarifas.hayAnterior,
                onNext: tarifas.siguiente,
                onPrevious: tarifas.anterior,
              }}
            />
          </section>
        </>
      )}
    </div>
  );
}

/** NuevoDeck crea una lista de precios. */
function NuevoDeck({
  hasDefault,
  onCancel,
  onCreated,
}: {
  hasDefault: boolean;
  onCancel: () => void;
  onCreated: (id: string) => void;
}) {
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(!hasDefault);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      const deck = await api.post<RateDeck>("/api/v1/rate-decks", {
        name: name.trim(),
        is_default: isDefault,
      });
      onCreated(deck.id);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo crear");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title="Nuevo rate deck"
      description="Una lista de precios. Las tarifas se cargan después, por CSV."
      onClose={onCancel}
    >
      <form className="space-y-4" onSubmit={(e) => void submit(e)}>
        <Field label="Nombre" hint="Como se llama la lista para quien la va a elegir.">
          <input
            className={inputClass}
            required
            autoFocus
            placeholder="Mayoreo 2026"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </Field>

        <label className="flex items-start gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={isDefault}
            onChange={(e) => setIsDefault(e.target.checked)}
          />
          <span>
            Por omisión
            <span className="mt-0.5 block text-xs text-slate-500">
              {hasDefault
                ? "Ya hay uno por omisión y solo puede haber uno: marcar este se lo quita al otro."
                : "Se aplica a las cuentas que no tengan deck propio."}
            </span>
          </span>
        </label>

        {error !== null && <ActionError message={error} />}

        <div className="flex flex-wrap gap-2">
          <Button type="submit" disabled={busy || name.trim() === ""}>
            {busy ? "Creando…" : "Crear deck"}
          </Button>
          <Button type="button" variant="secondary" onClick={onCancel}>
            Cancelar
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const CSV_HEADER =
  "prefijo,descripcion,precio_mxn_minuto,incremento_inicial,incremento_siguiente,cargo_conexion_mxn";

const CSV_EJEMPLO = `${CSV_HEADER}
*,Resto del mundo,9.00,60,60,0
52,México fijo,0.35,60,60,0`;

/**
 * ImportarTarifas carga precios en un deck, en dos pasos.
 *
 * # Por qué dos pasos y no un botón
 *
 * Porque un cambio de precios mal hecho **no se nota hasta la factura**. No
 * hay deshacer que valga: las tarifas se versionan por vigencia, así que una
 * importación equivocada ya cobró llamadas antes de que nadie la vea. Por eso
 * aquí no existe «importar»: existe «ver qué cambia» y, con lo que cambia
 * delante, «aplicar».
 *
 * El diálogo ayuda justo en eso. Antes el importador era una tarjeta más de la
 * página, entre el listado de decks y la tabla, y la vista previa aparecía
 * empujando el resto: se podía pulsar aplicar con media revisión fuera de la
 * pantalla, y no se veía sobre qué deck se estaba aplicando.
 */
function ImportarTarifas({
  deck,
  onCancel,
  onImported,
}: {
  deck: RateDeck;
  onCancel: () => void;
  onImported: (resumen: ImportSummary) => void;
}) {
  const [csv, setCSV] = useState("");
  const [preview, setPreview] = useState<ImportSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nombreDelArchivo, setNombreDelArchivo] = useState<string | null>(null);

  // Tocar el CSV descarta la revisión: aplicar una que ya no corresponde al
  // texto sería peor que no tener revisión.
  function cambiarCSV(value: string) {
    setCSV(value);
    setPreview(null);
  }

  async function run(dryRun: boolean) {
    setError(null);
    setBusy(true);
    try {
      const summary = await api.postText<ImportSummary>(
        `/api/v1/rate-decks/${deck.id}/rates/import?dry_run=${dryRun}`,
        "text/csv",
        csv,
      );
      if (dryRun) setPreview(summary);
      else onImported(summary);
    } catch (err) {
      setError(err instanceof ApiFailure ? err.error.message : "no se pudo importar");
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setNombreDelArchivo(file.name);
    cambiarCSV(await file.text());
  }

  const hayCambios = preview !== null && preview.added + preview.changed > 0;

  return (
    <Modal
      title={`Importar tarifas en «${deck.name}»`}
      description={
        preview === null
          ? "Primero se revisa qué cambiaría. Nada se aplica en este paso."
          : "Esto es lo que cambiaría. Nada se ha aplicado todavía."
      }
      width="lg"
      onClose={onCancel}
    >
      {/* Dos pasos, y el segundo sustituye al primero en lugar de añadirse
          debajo: con el CSV todavía en pantalla, la revisión queda tan abajo
          que se aplica sin haberla leído. */}
      {preview === null ? (
        <div className="space-y-4">
          <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Formato</p>
            <code className="mt-1.5 block overflow-x-auto whitespace-pre text-[11px] text-slate-300">
              {CSV_HEADER}
            </code>
            <p className="mt-2 text-xs text-slate-500">
              El prefijo va sin <code>+</code> y <code>*</code> es el comodín. Los incrementos
              son el redondeo en segundos: <code>60,60</code> cobra minutos completos.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <label className="cursor-pointer rounded-lg border border-white/15 bg-white/[0.04] px-3 py-1.5 text-sm text-slate-300 transition hover:bg-white/[0.09] hover:text-white">
              Elegir un archivo CSV
              <input
                type="file"
                accept=".csv,text/csv"
                className="sr-only"
                onChange={(e) => void onFile(e)}
              />
            </label>
            {nombreDelArchivo !== null && (
              <span className="text-xs text-slate-500">{nombreDelArchivo}</span>
            )}
            <span className="text-xs text-slate-600">o pégalo abajo</span>
          </div>

          <textarea
            className={`${inputClass} font-mono text-xs`}
            rows={10}
            spellCheck={false}
            placeholder={CSV_EJEMPLO}
            value={csv}
            onChange={(e) => cambiarCSV(e.target.value)}
          />

          {error !== null && <ActionError message={error} />}

          <div className="flex flex-wrap gap-2">
            <Button disabled={busy || csv.trim() === ""} onClick={() => void run(true)}>
              {busy ? "Revisando…" : "Ver qué cambia"}
            </Button>
            <Button variant="secondary" onClick={onCancel}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Resumen valor={preview.added} etiqueta="nuevas" tono="good" />
            <Resumen valor={preview.changed} etiqueta="cambian de precio" tono="warn" />
            <Resumen valor={preview.unchanged} etiqueta="sin cambio" tono="neutral" />
          </div>

          {hayCambios ? (
            <div className="rounded-lg border border-white/10">
              <div className="max-h-72 overflow-y-auto">
                <table className="w-full text-left text-xs">
                  <thead className="sticky top-0 border-b border-white/10 bg-[#11162a]">
                    <tr className="text-slate-400">
                      <th className="px-3 py-2 font-medium">Prefijo</th>
                      <th className="px-3 py-2 text-right font-medium">Antes</th>
                      <th className="px-3 py-2 text-right font-medium">Después</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {preview.changes.map((change) => (
                      <tr key={`${change.kind}-${change.prefix}`}>
                        <td className="px-3 py-1.5 font-mono text-slate-200">
                          <span
                            className={
                              "mr-1.5 " +
                              (change.kind === "added" ? "text-emerald-300" : "text-amber-300")
                            }
                          >
                            {change.kind === "added" ? "+" : "~"}
                          </span>
                          {change.prefix === "" ? (
                            <em className="text-slate-500">comodín</em>
                          ) : (
                            change.prefix
                          )}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-600">
                          {change.old_price ? money(change.old_price) : "—"}
                        </td>
                        <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-100">
                          {money(change.new_price)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {preview.changes.length < preview.added + preview.changed && (
                <p className="border-t border-white/10 px-3 py-2 text-xs text-slate-500">
                  Se listan {preview.changes.length} de {preview.added + preview.changed} cambios.
                </p>
              )}
            </div>
          ) : (
            <p className="rounded-lg border border-white/10 bg-white/[0.02] p-4 text-sm text-slate-400">
              Nada que aplicar: el CSV coincide con lo que ya está vigente.
            </p>
          )}

          {hayCambios && (
            <p className="text-xs text-slate-500">
              Al aplicar, las tarifas que cambian se cierran con la fecha de hoy y se insertan
              las nuevas. Las cerradas se conservan: una llamada se cobra con la tarifa que
              estaba vigente cuando empezó.
            </p>
          )}

          {error !== null && <ActionError message={error} />}

          <div className="flex flex-wrap gap-2">
            {hayCambios && (
              <Button disabled={busy} onClick={() => void run(false)}>
                {busy ? "Aplicando…" : `Aplicar ${preview.added + preview.changed} cambios`}
              </Button>
            )}
            <Button variant="secondary" onClick={() => setPreview(null)}>
              Volver a editar
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}

/** Resumen es una cifra de la revisión, con su etiqueta. */
function Resumen({
  valor,
  etiqueta,
  tono,
}: {
  valor: number;
  etiqueta: string;
  tono: "good" | "warn" | "neutral";
}) {
  const color = {
    good: "text-emerald-300",
    warn: "text-amber-300",
    neutral: "text-slate-400",
  }[tono];
  return (
    <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3">
      <p className={`text-2xl font-semibold tabular-nums ${valor === 0 ? "text-slate-600" : color}`}>
        {valor}
      </p>
      <p className="mt-0.5 text-xs text-slate-500">{etiqueta}</p>
    </div>
  );
}
